import React from 'react';
import { useIntl } from 'react-intl';
import { LEARNING_PHASES } from '../../constants/learningPhases';
import { useLearningFlow } from '../../hooks/useLearningFlow';
import CourseIntro from '../CourseIntro';
import GuidanceStep from '../GuidanceStep';
import FailStep from '../OutcomeStep/FailStep';
import LockedStep from '../OutcomeStep/LockedStep';
import SuccessStep from '../OutcomeStep/SuccessStep';
import QuestionnaireStep from '../QuestionnaireStep';
import { FlexColumn, FlexRow, Typography } from 'design-system';
import { SpinnerIcon } from 'design-system/components/Icons';
import LearningShell from '../../components/LearningShell';

export default function LearningFlow() {
  const intl = useIntl();
  const flow = useLearningFlow();
  const {
    course,
    packageLoading,
    packageError,
    phase,
    surveyId,
    attemptNumber,
    nextSubmitAttempt,
    maxAttempts,
    attemptsRemaining,
    passMarkPercent,
    quizResult,
    canRetry,
    startGuidance,
    startQuestionnaire,
    reviewGuidance,
    completeQuiz,
    retryQuiz,
  } = flow;

  if (packageLoading) {
    return (
      <LearningShell layout="intro" showWarning={false}>
        <FlexRow align="center" justify="center" fullWidth className="ld-course-intro">
          <FlexColumn align="center" fullWidth={false} gap="var(--ds-space-3)">
            <SpinnerIcon
              size={24}
              className="ds-icon--spin"
              aria-label={intl.formatMessage({ id: 'loading' })}
              trackFill="var(--ds-theme-loader-track-fill)"
              arcFill="var(--ds-theme-action-primary)"
              arc2Fill="var(--ds-theme-action-primary)"
            />
            <Typography component="p" variant="bodyMedium" color="ui-subtle">
              {intl.formatMessage({ id: 'ld-loading-module' })}
            </Typography>
          </FlexColumn>
        </FlexRow>
      </LearningShell>
    );
  }

  if (packageError || !course) {
    const errorMessage = packageError?.startsWith('ld-')
      ? intl.formatMessage({ id: packageError })
      : packageError;

    return (
      <LearningShell layout="intro" showWarning={false}>
        <FlexColumn align="center" fullWidth className="ld-course-intro">
          <Typography component="p" variant="bodyMedium" color="ui-subtle">
            {errorMessage || intl.formatMessage({ id: 'ld-unable-load-module' })}
          </Typography>
        </FlexColumn>
      </LearningShell>
    );
  }

  if (phase === LEARNING_PHASES.INTRO) {
    return <CourseIntro course={course} onStartGuidance={startGuidance} />;
  }

  if (phase === LEARNING_PHASES.GUIDANCE) {
    return <GuidanceStep course={course} onCompleteGuidance={startQuestionnaire} />;
  }

  if (phase === LEARNING_PHASES.QUIZ) {
    return (
      <QuestionnaireStep
        key={nextSubmitAttempt}
        course={course}
        surveyId={surveyId || undefined}
        onQuizComplete={completeQuiz}
      />
    );
  }

  if (phase === LEARNING_PHASES.SUCCESS && quizResult) {
    return <SuccessStep course={course} quizResult={quizResult} />;
  }

  if (phase === LEARNING_PHASES.FAIL && quizResult) {
    return (
      <FailStep
        course={course}
        quizResult={quizResult}
        passMarkPercent={passMarkPercent}
        attemptNumber={attemptNumber}
        maxAttempts={maxAttempts}
        attemptsRemaining={attemptsRemaining}
        canRetry={canRetry}
        onRetry={retryQuiz}
        onReviewGuidance={reviewGuidance}
      />
    );
  }

  if (phase === LEARNING_PHASES.LOCKED && quizResult) {
    return (
      <LockedStep
        course={course}
        quizResult={quizResult}
        passMarkPercent={passMarkPercent}
      />
    );
  }

  return null;
}
